// src/pages/Admin/Adherents/AchatsAdherent.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getUserIdByAdherentId } from "../../../services/adherentServices";
import { getReglementsByUser } from "../../../services/achatsServices";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

export default function AchatsAdherent({ adherentId }) {
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(false);

  // Format € (FR)
  const eur = (n) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
      Number.isFinite(+n) ? +n : 0
    );

  // Badges
  const statusClass = (s) => {
    switch (s) {
      case "Réglé":
        return "bg-emerald-100 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50";
      case "Non Réglé":
        return "bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800/50";
      case "En Attente":
        return "bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800/50";
      case "Annulé":
        return "bg-slate-800 text-white ring-slate-700 dark:bg-slate-800 dark:text-white dark:ring-slate-700";
      case "Remboursé":
        return "bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:ring-fuchsia-800/50";
      case "Partiellement Réglé":
        return "bg-emerald-50 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/50";
      default:
        return "bg-slate-100 text-slate-900 ring-slate-200 dark:bg-slate-800/40 dark:text-slate-100 dark:ring-slate-700/50";
    }
  };
  const typeReglementClass = (t) =>
    "bg-white text-slate-800 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700";

  const typeAchatLabel = (t) =>
    t === "licence-assurance-cotisation" ? "Licence / Cotisation" : t === "boutique" ? "Boutique" : t || "—";

  // Charger achats liés à TOUS les users qui gèrent cet adhérent
  useEffect(() => {
    if (!adherentId) return;
    (async () => {
      try {
        setLoading(true);
        const userObjects = await getUserIdByAdherentId(adherentId); // tableau d'objets utilisateur liés
        let all = [];
        for (const u of userObjects || []) {
          const userId = u?.UserAdherent?.user_id || u?.id || u?.user_id;
          if (!userId) continue;
          const arr = await getReglementsByUser(userId);
          if (Array.isArray(arr)) all = all.concat(arr);
        }
        // tri récents d'abord
        all.sort(
          (a, b) =>
            new Date(b?.date_achat || 0).getTime() - new Date(a?.date_achat || 0).getTime()
        );
        setAchats(all);
      } catch (e) {
        console.error("Erreur lors de la récupération des achats:", e);
        setAchats([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId]);

  // Résumé
  const { total, totalRegle, totalNonRegle } = useMemo(() => {
    const t = achats.reduce((s, x) => s + (parseFloat(x?.prix_total) || 0), 0);
    const tr = achats
      .filter((x) => x?.statut_reglement === "Réglé")
      .reduce((s, x) => s + (parseFloat(x?.prix_total) || 0), 0);
    const tnr = achats
      .filter((x) => x?.statut_reglement === "Non Réglé")
      .reduce((s, x) => s + (parseFloat(x?.prix_total) || 0), 0);
    return { total: t, totalRegle: tr, totalNonRegle: tnr };
  }, [achats]);

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Commandes & Règlements (utilisateur(s) gestionnaire)
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
              />
            ))}
          </div>
        ) : achats.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun achat trouvé pour les gestionnaires de cet adhérent.
          </div>
        ) : (
          <>
            {/* Résumé */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Kpi label="Total achats" value={eur(total)} />
              <Kpi label="Réglé" value={eur(totalRegle)} tone="ok" />
              <Kpi label="Non réglé" value={eur(totalNonRegle)} tone="warn" />
            </div>

            {/* Liste des achats */}
            <div className="grid grid-cols-1 gap-3">
              {achats.map((a) => {
                const isLicence = a?.type_achat === "licence-assurance-cotisation";
                const title =
                  a?.Article?.nom || (isLicence ? "Licence • Assurance • Cotisation" : "Achat");
                const thumb = a?.Article?.photo_url || null;
                const date =
                  a?.date_achat ? new Date(a.date_achat).toLocaleDateString("fr-FR") : "—";
                const userName =
                  a?.User ? `${a.User.prenom || ""} ${a.User.nom || ""}`.trim() : "—";
                return (
                  <article
                    key={`${a.id}-${a.date_achat}`}
                    className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white dark:bg-slate-900/60"
                  >
                    <div className="flex items-start gap-4">
                      {/* Vignette */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-center">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 px-2 text-center">
                            {isLicence ? "Licence / Cotisation" : "Article"}
                          </div>
                        )}
                      </div>

                      {/* Contenu principal */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <h4 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {title}
                          </h4>
                          {/* Statut */}
                          <span
                            className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 ${statusClass(
                              a?.statut_reglement
                            )}`}
                            title="Statut du règlement"
                          >
                            {a?.statut_reglement || "—"}
                          </span>
                        </div>

                        {/* Métadonnées */}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                            {date}
                          </span>
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-200 dark:ring-indigo-800/40">
                            {typeAchatLabel(a?.type_achat)}
                          </span>
                          {a?.type_reglement && (
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs ring-1 ${typeReglementClass(
                                a?.type_reglement
                              )}`}
                              title="Type de règlement"
                            >
                              {a.type_reglement}
                            </span>
                          )}
                          {a?.quantite > 1 && (
                            <span className="inline-flex items-center rounded-md px-2 py-0.5 bg-slate-50 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                              Qté&nbsp;: {a.quantite}
                            </span>
                          )}
                        </div>

                        {/* Message / note */}
                        {a?.message_achat && (
                          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                            {a.message_achat}
                          </p>
                        )}

                        {/* Utilisateur (gestionnaire) */}
                        <div className="mt-3 flex items-center gap-2">
                          <img
                            src={toAbsoluteUserPhotoUrl(a?.User?.photoURL) || "/img/user_avatar.jpg"}
                            alt={userName}
                            className="w-7 h-7 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          />
                          <span className="text-xs text-slate-600 dark:text-slate-300">
                            {userName}
                          </span>
                        </div>
                      </div>

                      {/* Prix à droite */}
                      <div className="shrink-0 text-right">
                        <div className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
                          {eur(a?.prix_total)}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* Petits composants */
function Kpi({ label, value, tone }) {
  const tones = {
    ok: "ring-emerald-200 bg-emerald-50 text-emerald-900 dark:ring-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-200",
    warn:
      "ring-amber-200 bg-amber-50 text-amber-900 dark:ring-amber-800/60 dark:bg-amber-900/30 dark:text-amber-200",
    base:
      "ring-slate-200 bg-slate-50 text-slate-900 dark:ring-slate-800/60 dark:bg-slate-900/30 dark:text-slate-100",
  };
  const cls = tone ? tones[tone] : tones.base;
  return (
    <div className={`rounded-xl p-3 ring-1 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-0.5 text-sm sm:text-base font-semibold">{value}</div>
    </div>
  );
}
